import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getAuth, sendEmailVerification, signOut, fetchSignInMethodsForEmail } from "firebase/auth";
import { getFirestore, doc, getDoc, updateDoc, query, collection, where, getDocs } from "firebase/firestore";
import { Box, Button, Typography, Stack, CircularProgress, Alert, Paper } from "@mui/material";
import HomepageHeader from "../../../components/UI/organisms/header/Header"
import { Home, ArrowBack, DescriptionOutlined } from "@mui/icons-material";

// Import Assets
import logo from "../../../assets/logo.png";
import Icon from "../../../components/UI/atoms/icon/Icon";
import Text from "../../../components/UI/atoms/text/Text";
import Snackbar from "../../../components/UI/atoms/snackbar/Snackbar";
import SplashScreen from "../../../components/UI/atoms/splash/SplashScreen";

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();
  const db = getFirestore();
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userMobile, setUserMobile] = useState("");
  const [hasMobile, setHasMobile] = useState(null); // null = not checked yet, true/false = checked
  const [verificationStatus, setVerificationStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendError, setResendError] = useState("");
  const [showSplash, setShowSplash] = useState(true);
  
  useEffect(() => {
    const checkUserStatus = async () => {
      // First, check URL for action code and apply it to sign in the user
      const urlParams = new URLSearchParams(window.location.search);
      const mode = urlParams.get('mode');
      const oobCode = urlParams.get('oobCode');
      
      // If coming back from verification link, apply the action code to complete verification
      if ((mode === 'verifyAndChangeEmail' || mode === 'verifyEmail') && oobCode) {
        try {
          // Apply the action code (this completes the email verification in Firebase Auth)
          const { applyActionCode } = await import('firebase/auth');
          await applyActionCode(auth, oobCode);
          
          console.log('✅ Email verification completed via action code');
          
          // After verification, check if user has pending email change and sync
          // The onAuthStateChanged in useAuth will handle the sync once user is signed in
          
          // Clean up URL parameters
          if (window.history && window.history.replaceState) {
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        } catch (error) {
          console.error('Error applying action code:', error);
        }
      }
      
      // Get email from location state or current user (with null check)
      const email = location.state?.email || (auth.currentUser ? auth.currentUser.email : null) || "";
      const fromSignIn = location.state?.fromSignIn;
      const userId = location.state?.userId;
      
      setUserEmail(email || "");

      // If user came from sign-in with pending email, get their mobile number
      if (fromSignIn && email && userId) {
        // Fetch user data to get mobile number
        try {
          const userDoc = await getDoc(doc(db, "users", userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const mobile = userData.mobile || "";
            setUserMobile(mobile);
            setHasMobile(!!mobile);
          }
        } catch (error) {
          console.error("Error fetching user mobile:", error);
        }
        // User has pending email verification, show the page
        setShowSplash(false);
        return;
      }

      // If no user is signed in and not from sign-in flow, redirect to sign-in
      if (!auth.currentUser) {
        navigate("/sign-in", { replace: true });
        return;
      }

      // Check if user has mobile number FIRST (before other checks) to prevent showing "Sign Out"
      // Also check if user is mobile-only and should be redirected
      let userDoc = null;
      try {
        userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Fetch mobile number for authenticated users immediately
          const mobile = userData.mobile || userData.contactNumber || "";
          setUserMobile(mobile);
          setHasMobile(!!mobile); // Set to true if has mobile, false if not
          
          // Check if mobile-only user
          const isMobileOnly = userData.emailVerified === "MobileOnly" || 
                             auth.currentUser.email?.includes("@mobileuser.dentalapp.com");
          
          // Skip verification for mobile-only users
          if (isMobileOnly) {
            navigate("/dashboard", { replace: true });
            return;
          }
        } else {
          setHasMobile(false); // No user doc found, no mobile
        }
      } catch (error) {
        console.error("Error fetching user mobile:", error);
        setHasMobile(false); // On error, assume no mobile
      }

      // Reload user to get latest emailVerified status before checking
      if (!auth.currentUser) {
        navigate("/sign-in", { replace: true });
        return;
      }
      
      try {
        await auth.currentUser.reload();
      } catch (error) {
        console.error("Error reloading user:", error);
        // If reload fails and user is null, redirect to sign-in
        if (!auth.currentUser) {
          navigate("/sign-in", { replace: true });
          return;
        }
      }

      // Check for pending email change and sync if verified
      if (!auth.currentUser) {
        navigate("/sign-in", { replace: true });
        return;
      }
      
      const currentAuthEmail = auth.currentUser.email;
      const isEmailVerified = auth.currentUser.emailVerified;
      
      if (userDoc && userDoc.exists()) {
        const userData = userDoc.data();
        const pendingEmailChange = userData.pendingEmailChange;
        const emailChangeStatus = userData.emailChangeStatus;
        
        // Check if there's a pending email change
        if (pendingEmailChange && emailChangeStatus === "pending") {
          // CRITICAL: First check if the pending email exists in Firebase Auth (verification happened in new tab)
          // This handles the case where user verified in a new tab but old tab still has old session
          try {
            const methods = await fetchSignInMethodsForEmail(auth, pendingEmailChange);
            if (methods && methods.length > 0) {
              // The pending email has been verified in Firebase Auth!
              // Sync Firestore with the verified email
              const userRef = doc(db, "users", auth.currentUser.uid);
              await updateDoc(userRef, {
                authEmail: pendingEmailChange,
                email: pendingEmailChange,
                emailVerified: "Yes",
                pendingEmailChange: null,
                emailChangeStatus: "completed",
                emailChangeTimestamp: null,
                emailChangeCancellationToken: null
              });
              
              // Redirect to dashboard
              navigate("/dashboard", { replace: true });
              return;
            }
          } catch (error) {
            console.error("Error checking pending email in Firebase Auth:", error);
            // Continue with other checks if this fails
          }
          
          // If current auth email matches the pending email and is verified, sync Firestore
          if (currentAuthEmail === pendingEmailChange && isEmailVerified) {
            // Email change has been verified! Sync Firestore
            try {
              const userRef = doc(db, "users", auth.currentUser.uid);
              await updateDoc(userRef, {
                authEmail: pendingEmailChange,
                email: pendingEmailChange,
                emailVerified: "Yes",
                pendingEmailChange: null,
                emailChangeStatus: "completed",
                emailChangeTimestamp: null,
                emailChangeCancellationToken: null
              });
            } catch (error) {
              console.error("Error syncing email change:", error);
            }
            
            // Redirect to dashboard
            navigate("/dashboard", { replace: true });
            return;
          }
          
          // If current auth email doesn't match pending email but is verified and different from Firestore
          // This could mean the verification changed the email to something else
          const firestoreEmail = userData.email || userData.authEmail;
          if (currentAuthEmail !== firestoreEmail && isEmailVerified) {
            // Email in Firebase Auth is verified but different from Firestore - sync it
            try {
              const userRef = doc(db, "users", auth.currentUser.uid);
              await updateDoc(userRef, {
                authEmail: currentAuthEmail,
                email: currentAuthEmail,
                emailVerified: "Yes",
                pendingEmailChange: null,
                emailChangeStatus: "completed",
                emailChangeTimestamp: null,
                emailChangeCancellationToken: null
              });
            } catch (error) {
              console.error("Error syncing email change:", error);
            }
            
            // Redirect to dashboard
            navigate("/dashboard", { replace: true });
            return;
          }
        }
        
        // If there's a mismatch between Firebase Auth email and Firestore email but no pending change
        // This could happen if sync failed earlier - sync it now
        const firestoreEmail = userData.email || userData.authEmail;
        if (currentAuthEmail !== firestoreEmail && isEmailVerified) {
          // Sync Firestore with Firebase Auth email
          try {
            const userRef = doc(db, "users", auth.currentUser.uid);
            await updateDoc(userRef, {
              authEmail: currentAuthEmail,
              email: currentAuthEmail,
              emailVerified: "Yes"
            });
          } catch (error) {
            console.error("Error syncing email:", error);
          }
          
          // Redirect to dashboard
          navigate("/dashboard", { replace: true });
          return;
        }
      }

      // If email is already verified (standard verification or email change already synced), redirect to dashboard
      if (isEmailVerified) {
        navigate("/dashboard", { replace: true });
        return;
      }
      
      // All checks complete, hide splash screen to show the page
      setShowSplash(false);
    };

    checkUserStatus();
    // Clear any suppression flag so future navigations work normally
    try { sessionStorage.removeItem('suppressPostSignInRedirect'); } catch (e) {}
  }, [navigate, location.state, auth, db]);

  const handleResendVerification = async () => {
    if (resendCooldown > 0) return;

    // Check if user is authenticated or came from sign-in with pending email
    const fromSignIn = location.state?.fromSignIn;
    const pendingEmail = location.state?.email;
    const userId = location.state?.userId;
    
    if (!auth.currentUser && !fromSignIn) return;

    setIsResending(true);
    setResendError("");
    
    try {
      if (auth.currentUser) {
        const currentEmail = auth.currentUser.email;
        const actionCodeSettings = {
          url: `${window.location.origin}/verify-email`,
          handleCodeInApp: true,
        };

        if (fromSignIn && pendingEmail && pendingEmail !== currentEmail) {
          // Email-change flow: resend to the new pending email
          const { verifyBeforeUpdateEmail } = await import('firebase/auth');
          await verifyBeforeUpdateEmail(auth.currentUser, pendingEmail, actionCodeSettings);
        } else {
          // Standard verification for current (unverified) email
          await sendEmailVerification(auth.currentUser, actionCodeSettings);
        }
      }
      
      setResendSuccess(true);
      setResendCooldown(60); // 60 seconds cooldown
      
      // Start cooldown timer
      const timer = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      setTimeout(() => setResendSuccess(false), 3000);
    } catch (error) {
      console.error("Error sending verification email:", error);
      
      // Handle Firebase rate limiting errors
      if (error.code === "auth/too-many-requests") {
        setResendError("Too many requests. Please wait before trying again.");
        setResendCooldown(120); // 2 minutes cooldown for rate limiting
        
        // Start cooldown timer
        const timer = setInterval(() => {
          setResendCooldown((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setResendError("Failed to send verification email. Please try again.");
        setTimeout(() => setResendError(""), 5000);
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleBackToSignIn = async () => {
    try {
      // If user is authenticated, sign them out
      if (auth.currentUser) {
        await signOut(auth);
      }
      // Navigate back to sign-in
      navigate("/sign-in", { replace: true });
    } catch (error) {
      console.error("Error signing out:", error);
      // Still navigate even if sign out fails
      navigate("/sign-in", { replace: true });
    }
  };

  const handleCheckVerification = async () => {
    setIsLoading(true);
    const fromSignIn = location.state?.fromSignIn;
    const userId = location.state?.userId;
    const pendingEmail = location.state?.email;
    
    try { 
      // FIRST: Check if user is signed in - if they are, check their email
      if (auth.currentUser) {
        try {
          await auth.currentUser.reload();
          if (!auth.currentUser) {
            setIsLoading(false);
            return;
          }
          const currentAuthEmail = auth.currentUser.email;
          const isEmailVerified = auth.currentUser.emailVerified;
          
          // Get user data from Firestore to check pending email change
          const userRef = doc(db, "users", auth.currentUser.uid);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const pendingEmailChange = userData.pendingEmailChange;
            
            // If user is signed in with the pending email and it's verified, sync and redirect
            if (pendingEmailChange && currentAuthEmail === pendingEmailChange && isEmailVerified) {
              // Update Firestore
              await updateDoc(userRef, {
                email: currentAuthEmail,
                authEmail: currentAuthEmail,
                emailVerified: "Yes",
                emailChangeStatus: "completed",
                pendingEmailChange: null,
                emailChangeTimestamp: null,
                emailChangeCancellationToken: null
              });
              
              setVerificationStatus("✅ Your email has been verified! Redirecting to dashboard...");
              setTimeout(() => {
                navigate("/dashboard", { replace: true });
              }, 1500);
              setIsLoading(false);
              return;
            }
            
            // If user is signed in but email doesn't match pending or not verified
            if (pendingEmailChange) {
              setVerificationStatus("⏳ Your email verification is still pending. Please check your inbox for the new email and click the verification link.");
              setTimeout(() => setVerificationStatus(""), 7000);
              setIsLoading(false);
              return;
            }
          }
        } catch (error) {
          console.error('Error checking current user:', error);
        }
      }
      
      // SECOND: Check URL for action code - if present, email was just verified
      const urlParams = new URLSearchParams(window.location.search);
      const mode = urlParams.get('mode');
      const oobCode = urlParams.get('oobCode');
      
      if ((mode === 'verifyAndChangeEmail' || mode === 'verifyEmail') && oobCode) {
        // They came from verification link - apply the action code and check verification
        try {
          const { applyActionCode } = await import('firebase/auth');
          await applyActionCode(auth, oobCode);
          console.log('✅ Action code applied successfully');
          
          // Store verification status in localStorage (works across tabs)
          const emailToVerify = location.state?.email || userEmail || new URLSearchParams(window.location.search).get('email') || '';
          localStorage.setItem('emailVerificationCompleted', 'true');
          localStorage.setItem('verifiedEmail', emailToVerify);
        } catch (error) {
          // Action code might already be applied or expired
          // If already applied, Firebase Auth already has the verified email
          if (error.code === 'auth/invalid-action-code' || error.code === 'auth/expired-action-code') {
            // Action code already used or expired - but email might already be verified
            const emailToVerify = location.state?.email || userEmail || new URLSearchParams(window.location.search).get('email') || '';
            localStorage.setItem('emailVerificationCompleted', 'true');
            localStorage.setItem('verifiedEmail', emailToVerify);
          }
          console.log('Action code application result:', error.message);
        }
        
        // After applying action code, email is verified - show success
        setVerificationStatus("✅ Your email has been verified! Please sign in with your new email to complete the process.");
        setTimeout(() => {
          navigate("/sign-in", {
            replace: true,
            state: {
              message: "Email verified! Please sign in with your new email address."
            }
          });
        }, 2000);
        setIsLoading(false);
        return;
      }
      
      // THIRD: User is not signed in - check if we can verify by querying Firestore
      // If emailChangeTimestamp is old enough, assume user clicked verification link
      if (!auth.currentUser) {
        try {
          // FIRST: Check localStorage for verification status (set when action code was applied)
          const verificationCompleted = localStorage.getItem('emailVerificationCompleted');
          const verifiedEmail = localStorage.getItem('verifiedEmail');
          const pageEmail = location.state?.email || userEmail;
          
          if (verificationCompleted === 'true' && verifiedEmail && verifiedEmail === pageEmail) {
            // Verification was completed - show success
            localStorage.removeItem('emailVerificationCompleted');
            localStorage.removeItem('verifiedEmail');
            
            setVerificationStatus("✅ Your email has been verified! Please sign in with your new email to complete the process.");
            setTimeout(() => {
              navigate("/sign-in", {
                replace: true,
                state: {
                  message: "Email verified! Please sign in with your new email address."
                }
              });
            }, 2000);
            setIsLoading(false);
            return;
          }
          
          // SECOND: No localStorage verification found - email is still pending
          // Only show success if verification was actually completed (via localStorage from action code)
          // Never assume verification based on time - only trust actual verification
          setVerificationStatus("⏳ Your email verification is still pending. Please check your inbox and click the verification link.");
          setTimeout(() => setVerificationStatus(""), 7000);
        } catch (error) {
          console.error('Error checking pending email:', error);
          setVerificationStatus("⏳ Your email verification is still pending. Please check your inbox and click the verification link.");
          setTimeout(() => setVerificationStatus(""), 7000);
        }
        setIsLoading(false);
        return;
      }
      
      if (auth.currentUser) {
        try {
          // Reload user to get latest emailVerified status
          await auth.currentUser.reload();
          if (!auth.currentUser) {
            setIsLoading(false);
            return;
          }
          const currentAuthEmail = auth.currentUser.email;
          const isEmailVerified = auth.currentUser.emailVerified;
          
          // Check Firestore for pending email change
          const userRef = doc(db, "users", auth.currentUser.uid);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const pendingEmailChange = userData.pendingEmailChange;
            const emailChangeStatus = userData.emailChangeStatus;
            
            const firestoreEmail = userData.email || userData.authEmail;
            
            // Check if there's a pending email change
            if (pendingEmailChange && emailChangeStatus === "pending") {
              // CRITICAL: First check if the pending email exists in Firebase Auth (verification happened in new tab)
              // This handles the case where user verified in a new tab but old tab still has old session
              try {
                const methods = await fetchSignInMethodsForEmail(auth, pendingEmailChange);
                if (methods && methods.length > 0) {
                  // The pending email has been verified in Firebase Auth!
                  console.log("Pending email verified in Firebase Auth (new tab) - syncing Firestore");
                  await updateDoc(userRef, {
                    authEmail: pendingEmailChange,
                    email: pendingEmailChange,
                    emailVerified: "Yes",
                    pendingEmailChange: null,
                    emailChangeStatus: "completed",
                    emailChangeTimestamp: null,
                    emailChangeCancellationToken: null
                  });
                  
                  // Redirect to dashboard
                  navigate("/dashboard", { replace: true });
                  return;
                }
              } catch (error) {
                console.error("Error checking pending email in Firebase Auth:", error);
                // Continue with other checks if this fails
              }
              
              // Case 1: Current auth email matches pending email AND is verified - perfect match
              if (currentAuthEmail === pendingEmailChange && isEmailVerified) {
                console.log("Case 1: Matching pending email and verified - syncing Firestore");
                await updateDoc(userRef, {
                  authEmail: pendingEmailChange,
                  email: pendingEmailChange,
                  emailVerified: "Yes",
                  pendingEmailChange: null,
                  emailChangeStatus: "completed",
                  emailChangeTimestamp: null,
                  emailChangeCancellationToken: null
                });
                
                navigate("/dashboard", { replace: true });
                return;
              }
              
              // Case 2: Current auth email doesn't match pending email BUT is verified AND different from Firestore
              if (currentAuthEmail !== firestoreEmail && isEmailVerified) {
                console.log("Case 2: Firebase Auth verified but different from Firestore - syncing", {
                  currentAuthEmail,
                  firestoreEmail,
                  pendingEmailChange
                });
                await updateDoc(userRef, {
                  authEmail: currentAuthEmail,
                  email: currentAuthEmail,
                  emailVerified: "Yes",
                  pendingEmailChange: null,
                  emailChangeStatus: "completed",
                  emailChangeTimestamp: null,
                  emailChangeCancellationToken: null
                });
                
                navigate("/dashboard", { replace: true });
                return;
              }
              
              // Case 3: Pending email change but not verified yet
              if (!isEmailVerified) {
                setVerificationStatus("⏳ Your email verification is still pending. Please check your inbox for the new email and click the verification link.");
                setTimeout(() => setVerificationStatus(""), 7000);
                setIsLoading(false);
                return;
              }
            }
            
            // Case 4: No pending email change but Firebase Auth email differs from Firestore (sync needed)
            if (currentAuthEmail !== firestoreEmail && isEmailVerified) {
              console.log("Case 4: No pending change but emails differ - syncing", {
                currentAuthEmail,
                firestoreEmail
              });
              await updateDoc(userRef, {
                authEmail: currentAuthEmail,
                email: currentAuthEmail,
                emailVerified: "Yes"
              });
              
              navigate("/dashboard", { replace: true });
              return;
            }
          }
          
          // Standard email verification check (for regular sign-up or email change already completed)
          if (isEmailVerified) {
            navigate("/dashboard", { replace: true });
          } else {
            setVerificationStatus("Your email is not yet verified. Please check your inbox and click the verification link.");
            // Clear the status message after 5 seconds
            setTimeout(() => setVerificationStatus(""), 5000);
          }
        } catch (error) {
          console.error("Error in handleCheckVerification:", error);
          setVerificationStatus("Unable to check verification status. Please try again.");
          setTimeout(() => setVerificationStatus(""), 5000);
        }
      } else if (fromSignIn && userId && pendingEmail) {
        // User came from sign-in with pending email (not authenticated)
        // Check if the pending email has been verified in Firebase Auth
        try {
          const userDoc = await getDoc(doc(db, "users", userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            
            // Check if Firebase Auth email has been updated to the pending email
            try {
              // Try to fetch sign-in methods for the pending email
              const methods = await fetchSignInMethodsForEmail(auth, pendingEmail);
              
              if (methods && methods.length > 0) {
                // Email exists in Firebase Auth! 
                // This means verifyBeforeUpdateEmail successfully updated it
                
                // Update Firestore to sync
                const userRef = doc(db, "users", userId);
                await updateDoc(userRef, {
                  authEmail: pendingEmail,
                  email: pendingEmail,
                  emailVerified: true,
                  pendingEmailChange: null,
                  emailChangeStatus: null,
                  emailChangeTimestamp: null
                });
                
                // Show success message
                setVerificationStatus("✅ Your email has been verified! You can now sign in with your email address.");
                setTimeout(() => {
                  navigate("/sign-in", { replace: true });
                }, 3000);
              } else {
                // Email not found in Firebase Auth - still pending
                setVerificationStatus("⏳ Your email verification is still pending. Please check your inbox and click the verification link.");
                setTimeout(() => setVerificationStatus(""), 7000);
              }
            } catch (error) {
              // Email not verified yet or error occurred
              console.error("Error checking email methods:", error);
              setVerificationStatus("⏳ Your email verification is still pending. Please check your inbox and click the verification link.");
              setTimeout(() => setVerificationStatus(""), 7000);
            }
          }
        } catch (error) {
          console.error("Error checking verification:", error);
          setVerificationStatus("Unable to check verification status. Please try again.");
          setTimeout(() => setVerificationStatus(""), 5000);
        }
      }
    } catch (error) {
      console.error("Error in handleCheckVerification:", error);
      setVerificationStatus("An error occurred while checking your verification status. Please try again.");
      setTimeout(() => setVerificationStatus(""), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SplashScreen isOpen={showSplash} />
      <Box sx={{ backgroundColor: "#fdf9f3", minHeight: "100vh" }}>
        <HomepageHeader/>

    <Box
      sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          pt: 15, // padding-top for header spacing
        }}
    >
      <Paper
        elevation={1}
        sx={{
          maxWidth: 500,
          width: "100%",
          textAlign: "center",
          p: 5,
          borderRadius: 2,
        }}
      >
          <Stack spacing={3}
          sx={{
            alignItems: "center"
          }}>
            {/* Logo */}
            <Box
              sx={{
                width: "60px",
                height: "60px",
                backgroundColor: "rgba(46, 45, 45, 0.1)",
                borderRadius: "150px",
                padding: "20px",
              }}
            >
              <Icon
                src={require("../../../assets/icons/mail.png")}
                size="large"/> 
            </Box>

            {/* Title */}
            <Box
              sx={{
                mt: "20px"
              }}>
              <Text
                text="Verify Your Email"
                variant="headerh2"/>
            </Box>

            {/* Message */}
            <Box>
              <Typography
                variant="signUpModalText"
              >
                Please check your email inbox at 
                <Typography
                  variant="signUpModalText"
                  sx={{
                    padding: "0 4px",
                    fontWeight: "600"
                  }}
                >
                  {userEmail}
                </Typography>
                  and click on the provided link to verify your email. If you didn't receive the email,
                <Typography
                  onClick={handleResendVerification}
                  // disabled={isResending || resendCooldown > 0}
                  variant={ resendCooldown ? "signUpModalResendDisabled" : "signUpModalResend"}
                  sx={{
                    textDecoration: "underline",
                    cursor: "pointer"
                  }}
                >
                  {isResending ? (
                    "sending..."
                  ) : resendCooldown > 0 ? (
                    `resend in ${resendCooldown}s`
                ) : (
                      "click here to resend."
                  )}
                </Typography>
              </Typography>
            </Box>

            {/* "Already verified?" text and Sign-in button */}
            <Typography
              sx={{
                color: "#2C251E",
                fontSize: "14px",
                fontWeight: "400",
                marginTop: "30px",
              }}
            >
              Already verified?
            </Typography>

              <Button 
                variant="contained" 
              onClick={async () => {
                // Sign out the user first before navigating to sign-in
                try {
                  if (auth.currentUser) {
                    await signOut(auth);
                  }
                } catch (error) {
                  console.error("Error signing out:", error);
                }
                navigate("/sign-in");
              }}
                sx={{ 
                color: "#FDF9F3",
                backgroundColor: "#C5A572",
                  fontSize: "14px",
                  borderRadius: "50px",
                  padding: "12px 30px",
                  width: "100%",
                  maxWidth: "280px",
                  textTransform: "none",
                marginTop: "10px",
                  "&:hover": {
                  backgroundColor: "#8F7049",
                },
              }}
            >
              Sign-in
            </Button>

            {/* Conditionally show "Sign-in as Mobile Number" button if user has both email and mobile */}
            {hasMobile === null ? (
              // Loading state - show loading indicator (this shouldn't happen now since we show the sign-in button above)
              null
            ) : hasMobile && userMobile ? (
              // User has both email and mobile - show "or" and mobile sign-in button
              <Stack
                spacing={2}
                sx={{
                  marginTop: "30px",
                  textAlign: "center",
                  alignItems: "center",
                }}
              >
              {/* "or" divider text */}
              <Typography
                sx={{
                  color: "#999",
                  fontSize: "14px",
                  fontWeight: "400",
                  margin: "0 !important",
                  padding: "0 !important",
                }}
              >
                or
              </Typography>

                <Button
                  variant="outlined"
                  onClick={handleBackToSignIn}
                  sx={{
                    color: "#C5A572",
                    borderColor: "#C5A572",
                    fontSize: "14px",
                    borderRadius: "50px",
                    padding: "12px 30px",
                    width: "100%",
                    maxWidth: "280px",
                    textTransform: "none",
                    marginTop: "4px !important",
                    "&:hover": {
                      backgroundColor: "rgba(197, 165, 114, 0.1)",
                      borderColor: "#8F7049",
                    },
                  }}
                >
                  {`Sign-in as ${userMobile}`}
                </Button>
            </Stack>
            ) : null}
          </Stack>
        
      
      </Paper>
    </Box>

      <Snackbar
        open={verificationStatus || resendError}
        message={verificationStatus || resendError || null}
        background={verificationStatus?.includes("✅") || verificationStatus?.includes("verified") ? "success" : "error"}
      />

      <Snackbar
        open={resendSuccess}
        message={"Verification email sent successfully!" || null}
        background="success"
      />

      </Box>
    </>
  );
};

export default VerifyEmail;
